import {
  currentRotation,
  presenterName,
  rotationRows,
  sessionNumberMap,
  sortByDate,
  todayISO,
  type Category,
  type GuestPresenter,
  type ISODate,
  type Member,
  type PresenterRef,
  type Rotation,
  type RotationRow,
  type Session,
  type Settings,
  type Summary,
} from "@love-cpmd/core";
import { toast } from "@love-cpmd/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import * as api from "./api";
import { ApiError } from "./api";
import { subscribeDb } from "./api/storage";

/** Every query lives under "db" so one invalidation refreshes all screens after a change. */
export const qk = {
  all: ["db"] as const,
  members: ["db", "members"] as const,
  guests: ["db", "guestPresenters"] as const,
  categories: ["db", "categories"] as const,
  sessions: ["db", "sessions"] as const,
  summaries: ["db", "summaries"] as const,
  rotations: ["db", "rotations"] as const,
  activity: ["db", "activity"] as const,
  settings: ["db", "settings"] as const,
  notifications: (userId: string) => ["db", "notifications", userId] as const,
  prefs: (userId: string) => ["db", "prefs", userId] as const,
};

export const useMembers = (enabled = true) => useQuery({ queryKey: qk.members, queryFn: api.getMembers, enabled });
export const useGuestPresenters = () => useQuery({ queryKey: qk.guests, queryFn: api.getGuestPresenters });
export const useCategories = () => useQuery({ queryKey: qk.categories, queryFn: api.getCategories });
export const useSessions = () => useQuery({ queryKey: qk.sessions, queryFn: api.getSessions });
export const useSummaries = () => useQuery({ queryKey: qk.summaries, queryFn: api.getSummaries });
export const useRotations = () => useQuery({ queryKey: qk.rotations, queryFn: api.getRotations });
export const useActivity = () => useQuery({ queryKey: qk.activity, queryFn: api.getActivity });
export const useSettings = () => useQuery({ queryKey: qk.settings, queryFn: api.getSettings });
export const useNotifications = (userId: string | null) =>
  useQuery({
    queryKey: qk.notifications(userId ?? "-"),
    queryFn: () => api.getNotifications(userId!),
    enabled: !!userId,
    // Online: other people's changes reach the bell within a minute.
    refetchInterval: api.API_MODE === "http" ? 60_000 : false,
  });
export const usePrefs = (userId: string | null) =>
  useQuery({ queryKey: qk.prefs(userId ?? "-"), queryFn: () => api.getPrefs(userId!), enabled: !!userId });

/** Prototype: keep screens in sync when another tab changes the mock data. (Online, queries refetch on focus.) */
export function useDbSync() {
  const qc = useQueryClient();
  useEffect(() => (api.API_MODE === "mock" ? subscribeDb(() => void qc.invalidateQueries({ queryKey: qk.all })) : undefined), [qc]);
}

/** Everything a screen needs to render sessions, joined and indexed once. */
export interface World {
  today: ISODate;
  members: Member[];
  membersById: Map<string, Member>;
  /** Active people who can present (not guests). */
  presenters: Member[];
  guests: GuestPresenter[];
  guestsById: Map<string, GuestPresenter>;
  categories: Category[];
  categoriesById: Map<string, Category>;
  activeCategories: Category[];
  sessions: Session[];
  sessionsById: Map<string, Session>;
  summaries: Summary[];
  summaryBySession: Map<string, Summary>;
  rotations: Rotation[];
  rotation?: Rotation;
  rotationRows?: RotationRow[];
  settings: Settings;
  numbers: Map<string, number>;
  name: (ref: PresenterRef | undefined, style?: "full" | "short") => string;
  memberName: (id: string | undefined, style?: "full" | "short") => string;
}

export function useWorld(): { world?: World; isLoading: boolean; isError: boolean; refetch: () => void } {
  const members = useMembers();
  const guests = useGuestPresenters();
  const categories = useCategories();
  const sessions = useSessions();
  const summaries = useSummaries();
  const rotations = useRotations();
  const settings = useSettings();
  const all = [members, guests, categories, sessions, summaries, rotations, settings];
  const ready = all.every((q) => q.data !== undefined);

  const world = useMemo<World | undefined>(() => {
    if (!ready) return undefined;
    const membersById = new Map(members.data!.map((m) => [m.id, m]));
    const guestsById = new Map(guests.data!.map((g) => [g.id, g]));
    const dir = { members: membersById, guests: guestsById };
    const sorted = sortByDate(sessions.data!);
    const rotation = currentRotation(rotations.data!);
    return {
      today: todayISO(),
      members: members.data!,
      membersById,
      presenters: members.data!.filter((m) => m.active && m.role !== "guest"),
      guests: guests.data!,
      guestsById,
      categories: categories.data!,
      categoriesById: new Map(categories.data!.map((c) => [c.id, c])),
      activeCategories: categories.data!.filter((c) => c.active),
      sessions: sorted,
      sessionsById: new Map(sorted.map((s) => [s.id, s])),
      summaries: summaries.data!,
      summaryBySession: new Map(summaries.data!.map((s) => [s.sessionId, s])),
      rotations: rotations.data!,
      rotation,
      rotationRows: rotation ? rotationRows(rotation, membersById, sorted) : undefined,
      settings: settings.data!,
      numbers: sessionNumberMap(sorted),
      name: (ref, style = "full") => presenterName(ref, dir, style),
      memberName: (id, style = "full") => (id ? presenterName({ kind: "member", id }, dir, style) : "Someone"),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, members.data, guests.data, categories.data, sessions.data, summaries.data, rotations.data, settings.data]);

  return {
    world,
    isLoading: !ready && !all.some((q) => q.isError),
    isError: all.some((q) => q.isError),
    refetch: () => all.forEach((q) => void q.refetch()),
  };
}

export interface ActionOptions<TResult> {
  success?: string | ((result: TResult) => string | undefined);
  /** Error codes the calling form shows inline (no toast). */
  inline?: ApiError["code"][];
  onSuccess?: (result: TResult) => void;
}

/**
 * Mutation wrapper: invalidates data, shows a success toast, and on unexpected errors
 * a toast with "Try again" (plan: "Error: toast dengan tombol Coba lagi; isian form tetap tersimpan").
 */
export function useAction<TArgs extends unknown[], TResult>(fn: (...args: TArgs) => Promise<TResult>, options: ActionOptions<TResult> = {}) {
  const qc = useQueryClient();
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const mutation = useMutation<TResult, Error, TArgs>({
    mutationFn: (args) => fn(...args),
    onSuccess: (result) => {
      void qc.invalidateQueries({ queryKey: qk.all });
      const { success, onSuccess } = optionsRef.current;
      const message = typeof success === "function" ? success(result) : success;
      if (message) toast.success(message);
      onSuccess?.(result);
    },
    onError: (error, args) => {
      void qc.invalidateQueries({ queryKey: qk.all });
      const inline = optionsRef.current.inline ?? ["VALIDATION", "DATE_TAKEN", "CONFLICT"];
      if (error instanceof ApiError && inline.includes(error.code)) return;
      toast.error(error.message || "Something went wrong.", {
        action: error instanceof ApiError && error.code !== "STALE" && error.code !== "FORBIDDEN" ? { label: "Try again", onClick: () => mutationRef.current.mutate(args) } : undefined,
      });
    },
  });
  const mutationRef = useRef(mutation);
  mutationRef.current = mutation;
  return {
    run: (...args: TArgs) => mutation.mutateAsync(args),
    /** Fire-and-forget variant; errors are handled by the toast. */
    fire: (...args: TArgs) => mutation.mutate(args),
    pending: mutation.isPending,
    error: mutation.error instanceof ApiError ? mutation.error : mutation.error ? new ApiError("VALIDATION", mutation.error.message) : null,
    reset: mutation.reset,
  };
}

export { ApiError };
